"use client"
import { graphql, type OperationContext } from "@opensea/graphql"
import {
  classNames,
  EmptyState,
  FlexCenter,
  FlexColumn,
  ScrollingPaginator,
  TextBody,
  type TextProps,
} from "@opensea/ui-kit"
import { TTL } from "@opensea/utils"
import { useTranslations } from "next-intl"
import { usePaginatedQuery } from "@/hooks/api/use-paginated-query"
import { withSuspense } from "../suspense/WithSuspense"
import { TraitFilterPanel, TraitFilterPanelFragment } from "./TraitFilterPanel"
import {
  TraitFiltersSectionSkeleton,
  TraitFiltersSectionSkeletonRows,
} from "./TraitFiltersSectionSkeleton"

const PAGE_SIZE = 50

const TraitFiltersSectionQuery = graphql(
  `
    query TraitFiltersSectionQuery(
      $slug: String!
      $limit: Int!
      $cursor: String
    ) {
      collectionBySlug(slug: $slug) {
        __typename
        ... on Collection {
          id
          attributes(limit: $limit, cursor: $cursor) {
            items {
              traitType
              ...TraitFilterPanelFragment
            }
            nextPageCursor
          }
        }
      }
    }
  `,
  [TraitFilterPanelFragment],
)

type Props = {
  collectionSlug: string
  defaultOpen?: boolean
  // Used to disable selecting traits with high item counts that BE does not support filtering by on some queries (activity)
  disableHighItemCounts?: boolean
  className?: string
  overrides?: {
    title?: Partial<TextProps>
  }
}

const CONTEXT: Partial<OperationContext> = {
  ttl: TTL["5m"],
}

// TODO: Add virtualization
export const TraitFiltersSection = withSuspense(
  function TraitFiltersSection({
    collectionSlug,
    className,
    defaultOpen,
    disableHighItemCounts,
    overrides,
  }: Props) {
    const { items, pagination, error } = usePaginatedQuery(
      {
        query: TraitFiltersSectionQuery,
        variables: { slug: collectionSlug, limit: PAGE_SIZE },
        context: CONTEXT,
      },
      v =>
        v?.collectionBySlug?.__typename === "Collection"
          ? v.collectionBySlug.attributes
          : undefined,
      { pageSize: PAGE_SIZE, throwOnError: false },
    )

    const t = useTranslations("TraitFiltersSection")

    function getEmptyState() {
      if (error) {
        return <EmptyState className={className} variant="no-data" />
      }
      return null
    }

    const emptyState = getEmptyState()

    if (items.length === 0) {
      return null
    }

    return (
      <FlexColumn
        className={classNames("scrollbar-hidden overflow-y-auto", className)}
      >
        <FlexCenter className="mb-1 min-h-8">
          <TextBody size="sm" {...(overrides?.title ? overrides.title : {})}>
            {t("title")}
          </TextBody>
        </FlexCenter>

        {emptyState ? (
          emptyState
        ) : (
          <FlexColumn className="gap-0.5">
            {items.map(attribute => {
              return attribute === undefined ? null : (
                <TraitFilterPanel
                  attribute={attribute}
                  defaultOpen={defaultOpen}
                  disableHighItemCounts={disableHighItemCounts}
                  key={attribute.traitType}
                />
              )
            })}

            {pagination.isLoadingNext ? (
              <TraitFiltersSectionSkeletonRows />
            ) : null}
            {pagination.hasNext ? (
              <ScrollingPaginator
                hasNext={pagination.hasNext}
                isLoadingNext={pagination.isLoadingNext}
                loadNext={pagination.loadNext}
              />
            ) : null}
          </FlexColumn>
        )}
      </FlexColumn>
    )
  },
  { fallback: <TraitFiltersSectionSkeleton /> },
)
